/**
 * Echo Hunt -- the judge-loop inspector (P10 follow-up): bullet-by-bullet repair history, plus a manual
 * per-bullet retry with a user-chosen remedy.
 *
 * ## Why this exists
 *
 * The loop's round-level summary ("could NOT be made stranger-blind after 3 attempts") says THAT it failed,
 * and nothing about why or where. When the failure is "one bullet is obvious", that is the wrong resolution
 * of information: the count tells you a repair was spent, not which bullet ate it, what the model wrote, or
 * whether the retention gate threw it away. This panel shows, for every bullet, each attempt's remedy, its
 * outcome, and the before/after text -- including attempts that were REJECTED, which are otherwise invisible.
 *
 * ## Labels
 *
 * This shows `sourceKind`, which the hunt deliberately never exposes before the reveal (§16.0). That is
 * acceptable here and the user asked for it explicitly: it is a debugging surface for someone building and
 * tuning the pipeline, not for someone playing. It is reachable only from the reveal (a finished round) and
 * from the Settings tab's dev area, and the panel itself says so in a banner so nobody mistakes it for a
 * normal view. Nothing stored on the round changes; this only reads.
 *
 * ## The retry
 *
 * "Retry this bullet with THIS remedy" is a forced repair (`RetrySlotRepair_Live`), not a re-run of the
 * loop's heuristic -- the heuristic already picked something and it did not work, so the useful experiment is
 * the other remedy. The new attempt is appended to the bullet's history (its `attemptIndex` continues past the
 * automatic attempts) and the story is re-judged ONCE so the AUC shown is the result of this one change.
 *
 * The round's stored `judgeSummary` is deliberately NOT overwritten: it describes the round the player was
 * given, and a manual experiment on one bullet would otherwise make it claim a difficulty the stored story
 * never had. The re-judge's numbers are shown in the panel only.
 */
import React, {useState} from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, Column, Row, Select, Text} from "react-vcomponents";
import {ES, InfoButton} from "web-vcore";
import {ScrollView} from "react-vscrollview";
import {ProgressSpinner} from "../../../Utils/ReactComponents/ProgressSpinner.js";
import {GetMyEchoRounds} from "../../../EchoHunt/Round_Live.js";
import {GetMyEchoCorpusBullets} from "../../../EchoHunt/Index_Live.js";
import {
	ResolveRoundCharacter,
	ResolveRoundWorld,
	RetrySlotRepair_Live,
	RoundCharacterIsPlaceholder,
} from "../../../EchoHunt/Judge_Live.js";
import {ComputeJudgeMetric, DescribeRepair, JudgeMetric, NormalizeBand, ResolveSlotSandboxText} from "../../../EchoHunt/Judge.js";
import {DescribeCautionVsOpportunity} from "../../../EchoHunt/Prompts/Weave.js";
import {FindPhraseLeaks} from "../../../EchoHunt/PhraseOverlap.js";
import {JudgeRepair, RoundSlot, SlotRepairAttempt} from "../../../EchoHunt/Types.js";
import {EchoRound} from "../../../Server/DB/echoRounds/@EchoRound.js";

// the panel
// ==========

export const JudgeInspectorPanel = observer_mgl((props: {
	/** the round to inspect; null shows a round picker instead */
	round: EchoRound|null,
	/** hide the "close" affordance when this is embedded in a tab rather than shown as an overlay */
	embed?: boolean,
	onClose?: ()=>void,
})=>{
	const {round, embed, onClose} = props;

	if (round == null) {
		return <RoundPicker/>;
	}

	return (
		<ScrollView className="selectable" style={ES({flex: 1})} contentStyle={{display: "flex", flexDirection: "column", gap: 8}}>
			<Row center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold", fontSize: 14}}>Judge-loop inspector</Text>
				<InfoButton text={[
					"This shows which bullets came from your dreams and which were generated, plus every repair the loop",
					"tried on each one. That information is hidden during play on purpose (§16.0) -- this is a pipeline",
					"debugging view, not a game view.",
					"",
					"Retry re-runs ONE forced remedy on ONE bullet and then re-judges the story once, so the AUC you see",
					"is the result of that single change. The attempt is appended to the bullet's history.",
					"",
					"Retries do not overwrite the round's stored summary: that describes the round you were given.",
				].join("\n")}/>
				{!embed && onClose != null && <Button text="Close" onClick={onClose}/>}
			</Row>

			<Row center style={{gap: 8, padding: 6, borderRadius: 4, background: "rgba(120,40,0,.5)", border: "1px solid orange"}}>
				<Text style={{fontSize: 12, color: "orange"}}>
					Debug view: labels are visible and repairs here are real. Do not use this before submitting a round you intend to play.
				</Text>
			</Row>

			<RoundSummary round={round}/>
			<StoryContext round={round}/>

			<Column style={{gap: 8}}>
				{(round.slots ?? []).map(slot=>
					<BulletInspector key={slot.slotId} round={round} slot={slot}/>,
				)}
			</Column>
		</ScrollView>
	);
});

// story context (world + character)
// ==========

/** The world and character the round was woven and repaired against (§8.2, §8.5).
 *
 * Both matter when reading a repair's outcome: `rewriteReal` is told to make a bullet "fit this world better"
 * and is given the character for tone, so "why did the model write THAT" is often answered by the bible on
 * screen. The short fields (name, disposition) are inline; the world bible and our notes are behind a toggle,
 * since a bible runs to ~150 words and would otherwise push the bullet list off the screen.
 */
const StoryContext = observer_mgl((props: {round: EchoRound})=>{
	const {round} = props;
	const [expanded, setExpanded] = useState(false);

	const world = ResolveRoundWorld(round);
	const character = ResolveRoundCharacter(round);
	const characterIsPlaceholder = RoundCharacterIsPlaceholder(round);

	return (
		<Column style={{gap: 3, padding: 8, borderRadius: 5, background: "rgba(255,255,255,.05)"}}>
			<Row center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold", fontSize: 12}}>Context</Text>
				<Text style={{fontSize: 12}}>{`World: ${world.name}`}</Text>
				<Text style={{fontSize: 12, opacity: .8}}>
					{`Character: ${character.name} (${DescribeCautionVsOpportunity(character.values_cautionVsOpportunity)})`}
				</Text>
				<Button text={expanded ? "Hide details" : "Show details"} onClick={()=>setExpanded(!expanded)}/>
			</Row>

			{characterIsPlaceholder &&
				<Text style={{fontSize: 11, color: "orange"}}>
					{"No character snapshot on this round's story, so the name below is a generated stand-in -- a repair on this"
						+ " round was given a different character than the one it was written for."}
				</Text>}

			{expanded && (
				<Column style={{gap: 4, marginTop: 4}}>
					<Text style={{fontSize: 11, opacity: .6}}>{`world bible · craziness tolerance ${world.crazinessTolerance.toFixed(2)}`}</Text>
					<Text style={{fontSize: 12, opacity: .9}}>{world.bible}</Text>
					<Text style={{fontSize: 11, opacity: .6}}>{"character notes"}</Text>
					<Text style={{fontSize: 12, opacity: .9}}>{character.personalityNotes}</Text>
					{round.frame != null &&
						<Text style={{fontSize: 11, opacity: .5}}>{`frame: ${round.frame}`}</Text>}
				</Column>
			)}
		</Column>
	);
});

// round picker
// ==========

/** Lets the inspector be used from the Settings tab, where there is no round in hand. Lists recent rounds
 * newest-first; a round is inspectable whether or not it was revealed, because a failure is worth looking at
 * before the player sees the result. */
const RoundPicker = observer_mgl(()=>{
	const rounds = GetMyEchoRounds().slice().sort((a, b)=>b.createdAt - a.createdAt);
	const [selectedId, setSelectedId] = useState<string|null>(null);

	const selected = rounds.find(a=>a.id == selectedId) ?? null;
	if (selected != null) {
		return <JudgeInspectorPanel round={selected} embed={true}/>;
	}

	if (rounds.length == 0) {
		return <Text style={{opacity: .7}}>No rounds yet -- build one on the Play tab, then come back here to inspect it.</Text>;
	}

	return (
		<Column style={{gap: 6}}>
			<Text style={{fontWeight: "bold"}}>Judge-loop inspector</Text>
			<Text style={{fontSize: 12, opacity: .7}}>Pick a round to see what the stranger filter tried on each bullet.</Text>
			{rounds.slice(0, 12).map(round=>
				<Row key={round.id} center style={{gap: 10}}>
					<Button text={new Date(round.createdAt).toLocaleString()} onClick={()=>setSelectedId(round.id)}/>
					<Text style={{fontSize: 11, opacity: .6}}>
						{`${(round.slots ?? []).length} bullets · ${DescribeRoundVerdict(round)}`}
					</Text>
				</Row>,
			)}
		</Column>
	);
});

// round summary
// ==========

const RoundSummary = observer_mgl((props: {round: EchoRound})=>{
	const {round} = props;
	const summary = round.judgeSummary ?? null;
	const judgeLog = round.judgeLog ?? [];
	const attempts = [...new Set(judgeLog.map(a=>a.attemptIndex ?? 0))].sort((a, b)=>a - b);

	return (
		<Column style={{gap: 4, padding: 8, borderRadius: 5, background: "rgba(255,255,255,.05)"}}>
			{summary == null
				? <Text style={{opacity: .75}}>This round has no judge-loop summary (it was never judged).</Text>
				: (
					<>
						<Text style={{fontWeight: "bold", color: summary.landedInBand ? "lightgreen" : "orange"}}>
							{summary.landedInBand
								? `Landed in band after ${summary.attempts} attempt(s)`
								: `Could NOT be made stranger-blind after ${summary.attempts} attempt(s)`}
						</Text>
						<Text style={{fontSize: 12, opacity: .85}}>
							{`band ${summary.bandMin.toFixed(2)}-${summary.bandMax.toFixed(2)}`
								+ ` · final AUC ${summary.auc == null ? "n/a" : summary.auc.toFixed(3)}`
								+ ` · miscategorized ${summary.miscategorizationPct == null ? "n/a" : `${summary.miscategorizationPct.toFixed(0)}%`}`
								+ ` · held-out AUC ${summary.heldOutAuc == null ? "n/a" : summary.heldOutAuc.toFixed(3)}`}
						</Text>
						{summary.resistingSlotIds.length > 0 &&
							<Text style={{fontSize: 12, color: "orange"}}>
								{`${summary.resistingSlotIds.length} bullet(s) resisted hiding -- see the red rows below (§9.4).`}
							</Text>}
					</>
				)}

			{/* attempt-by-attempt AUC, so a "it got worse" repair is visible */}
			{attempts.length > 1 &&
				<Column style={{gap: 2, marginTop: 4}}>
					<Text style={{fontSize: 12, fontWeight: "bold"}}>Per attempt</Text>
					{attempts.map(attemptIndex=>{
						// the held-out judge is stamped with the LAST attempt's index for storage, but it is not part
						// of that attempt's loop -- counting it here would read as "attempt 2 had an extra judge"
						const entries = judgeLog.filter(a=>(a.attemptIndex ?? 0) == attemptIndex && a.judge != "heldOut");
						const meanAuc = MeanOf(entries.map(a=>a.auc).filter((a): a is number=>a != null));
						const repair = entries.map(a=>a.repair).find(a=>a != null && a != "none") ?? null;
						return (
							<Text key={attemptIndex} style={{fontSize: 12, opacity: .85}}>
								{`attempt ${attemptIndex + 1}: `
									+ (meanAuc == null ? "no usable score" : `AUC ${meanAuc.toFixed(3)}`)
									+ ` · ${entries.length} judge call(s)`
									+ (repair == null ? "" : ` · then ${DescribeRepair(repair)}`)}
							</Text>
						);
					})}
				</Column>}
		</Column>
	);
});

// one bullet
// ==========

const BulletInspector = observer_mgl((props: {round: EchoRound, slot: RoundSlot})=>{
	const {round, slot} = props;
	const isReal = slot.sourceKind == "real";
	// the loop's scores for this bullet, per attempt, so "the judge was sure about this one" is readable
	const scores = (round.judgeLog ?? [])
		.filter(a=>a.judge != "heldOut" && slot.slotId in a.scores)
		.map(a=>({attemptIndex: a.attemptIndex ?? 0, judge: a.judge, score: a.scores[slot.slotId]}));

	/** The same scores, grouped by attempt, so a label can be placed beside the TEXT that produced it. */
	const judgedByAttempt = JudgedTextsByAttempt(slot, scores);

	const resisting = (round.judgeSummary?.resistingSlotIds ?? []).includes(slot.slotId);
	const allHistory = slot.repairHistory ?? [];
	/** The LOOP attempts happen BEFORE the round is shown (they are part of producing `finalText`), so they
	 * belong between `wovenText` and `what the player read` in the display order. */
	const loopHistory = allHistory.filter(a=>a.source == "loop");
	/** MANUAL attempts happen AFTER the round was played, so they belong at the very end. */
	const manualHistory = allHistory.filter(a=>a.source == "manual");
	const playedText = DisplayText(slot);
	const sandboxText = ResolveSlotSandboxText(slot);
	/** For a real bullet, the pre-transformation journal text (§4.2), read from the corpus row. Null when the
	 * slot is a fake (there is no original) or the row has gone missing. */
	const originalText = isReal ? GetOriginalBulletText(slot.sourceId) : null;
	/** The step whose text the player ended up reading, so step 5 can point back rather than restate it. */
	const playedFrom = DescribePlayedTextSource(slot, originalText, loopHistory);
	/** 5+-word runs the played text copies from its predecessor (2026-10-21): the source dream's raw journal
	 * text for a real, the generator's text for a fake. Same check the reshape enforces, re-run on the FINAL text
	 * so a run the weave or a repair reintroduced is visible too. */
	const playedLeaks = FindPhraseLeaks(playedText, GetLeakReferencesForSlot(round, slot));

	return (
		<Column style={{
			padding: 8,
			borderRadius: 5,
			background: "rgba(255,255,255,.05)",
			// the resisting bullets are the ones §9.4 says are the most stranger-detectable content
			borderLeft: `3px solid ${resisting ? "tomato" : (isReal ? "lightgreen" : "rgba(255,255,255,.25)")}`,
		}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontSize: 11, fontWeight: "bold", color: isReal ? "lightgreen" : "rgba(255,255,255,.55)"}}>
					{isReal ? "DREAM" : "LLM"}
				</Text>
				<Text style={{fontSize: 11, opacity: .5}}>{slot.slotId}</Text>
				{slot.heldoutScore != null &&
					<Text style={{fontSize: 11, opacity: .6}}>{`held-out p=${slot.heldoutScore.toFixed(2)}`}</Text>}
				{scores.length > 0 &&
					<Text style={{fontSize: 11, opacity: .6}}>
						{`judge: ${DescribeAttemptScores(scores)}`}
					</Text>}
				{slot.rewriteCount > 0 && <Text style={{fontSize: 11, opacity: .6}}>{`${slot.rewriteCount} rewrite(s)`}</Text>}
				{slot.resampleCount > 0 && <Text style={{fontSize: 11, opacity: .6}}>{`${slot.resampleCount} resample(s)`}</Text>}
				{resisting && <Text style={{fontSize: 11, color: "tomato"}}>the judge was sure</Text>}
				<InfoButton text={[
					"What these numbers mean, for this one bullet:",
					"",
					"DREAM / LLM -- the truth. Whether this sentence really came from one of your dreams, or was generated.",
					"The id beside it is the slot id (random, assigned after shuffling).",
					"",
					"judge: 0.87, 0.82; 0.71 ... -- the LOOP judges' P(dream-sourced) for this bullet, one number per judge call,",
					"in attempt order. 1.0 = 'certainly a real remembered experience', 0.0 = 'certainly written'. Two judges",
					"are called per attempt by default. A COMMA separates two judges scoring the SAME text; a SEMICOLON starts",
					"a new attempt, numbered when there is more than one (#1, #2).",
					"",
					"An unchanged bullet can still show several attempts (#1 0.70, 0.80; #2 0.82, 0.82), because the loop repairs",
					"only ONE bullet per attempt but re-judges the WHOLE story each time. So this sentence was read twice -- the",
					"difference between the two rows is the effect of the OTHER bullet's repair on this one, not of anything done",
					"to it. Four numbers with no semicolon would instead mean four judge calls in a single attempt.",
					"",
					"The same 'judge:' label also appears beside each version of the bullet that a judge actually read (the",
					"woven text, then each repair's result, then the text the player read), so it is visible which exact text",
					"earned which number. The original journal text and the reshaped form carry no label when they were never",
					"judged directly -- the loop scores the woven story, not the intermediate forms.",
					"",
					"'what the player read' says 'unchanged from <step>' when its text is one already shown above, so the same",
					"sentence and its numbers are not printed twice.",
					"",
					"held-out p -- the HELD-OUT judge's score for the same bullet. That judge never took part in the repair",
					"loop, so it is the honest estimate: if the loop overfitted to its own judges, the loop numbers will look",
					"harmless while this one still separates the two classes.",
					"",
					"How to read the pair: for a DREAM bullet, a high 'judge' number is expected and fine as long as it sits",
					"near the FAKES' numbers too. What matters is the GAP between this bullet's number and the other class's,",
					"not the number alone -- which is exactly what the round-level AUC in the summary measures.",
					"",
					"rewrite(s) / resample(s) -- how many times the loop changed this bullet to hide it. A rewrite keeps the",
					"event and only rewords it into the world; a resample replaces the whole bullet with a different one.",
					"Counts above are from the LOOP only -- your manual retries appear under 'repair attempts' below and never",
					"count here, because they do not change the round you actually played.",
					"",
					"'the judge was sure' -- this bullet is on the §9.4 list: a judge confidently classified it correctly, so it",
					"is among the most stranger-detectable content in the round. For a DREAM bullet that is the interesting",
					"failure; for a fake it just means the fake was obvious.",
				].join("\n")}/>
			</Row>

			{/* ===== the pipeline, in the order it actually happened (§6, §8.1, §9.3) ===== */}

			{/* 1. the journal bullet, before any transformation. Real slots only -- a fake has no original. */}
			{isReal &&
				<Column mt={4} style={{gap: 2}}>
					<Row center style={{gap: 8, flexWrap: "wrap"}}>
						<Text style={{fontSize: 11, opacity: .6}}>{"1. original (journal text, before any transformation)"}</Text>
						<TextScoreLabel scores={scores} judgedByAttempt={judgedByAttempt} text={originalText}/>
					</Row>
					{originalText != null
						? <Text style={{opacity: .85}}>{originalText}</Text>
						// saying so beats silently omitting the step, which would read as "this bullet had no original"
						: <Text style={{fontSize: 12, opacity: .5}}>{"(corpus row not found, so the original text is unavailable)"}</Text>}
				</Column>}

			{/* 2. the shared reshape with the seeded words (§6). Always shown, like the woven step -- a no-op
			    reshape is a fact worth seeing (the round was unseeded, or the words already fit). */}
			{slot.reshapedText.length > 0 &&
				<Column mt={4} style={{gap: 2}}>
					<Row center style={{gap: 8, flexWrap: "wrap"}}>
						<Text style={{fontSize: 11, opacity: .6}}>
							{`2. reshaped (§6, seeded with ${slot.reshapeWords.length > 0 ? slot.reshapeWords.join(", ") : "nothing -- unseeded round"})`}
						</Text>
						{originalText != null && slot.reshapedText == originalText
							? <Text style={{fontSize: 11, opacity: .6}}>{"unchanged from 1. original"}</Text>
							: <TextScoreLabel scores={scores} judgedByAttempt={judgedByAttempt} text={slot.reshapedText}/>}
					</Row>
					<Text style={{opacity: .85}}>{slot.reshapedText}</Text>
				</Column>}

			{/* 3. the weaver's rebridging (§8.1). Shown even when it is where the pipeline STOPPED -- the played
			    text then points back here, rather than this attempt vanishing as if it never happened. */}
			{slot.wovenText.length > 0 &&
				<Column mt={4} style={{gap: 2}}>
					<Row center style={{gap: 8, flexWrap: "wrap"}}>
						<Text style={{fontSize: 11, opacity: .6}}>{"3. woven (§8.1, joined into the story)"}</Text>
						<TextScoreLabel scores={scores} judgedByAttempt={judgedByAttempt} text={slot.wovenText}/>
					</Row>
					<Text style={{opacity: .85}}>{slot.wovenText}</Text>
				</Column>}

			{/* 4. the LOOP's repairs (§9.3). These happened BEFORE the round was shown, which is why they sit here
			    rather than at the end: `finalText` below already includes their effect. */}
			{loopHistory.length > 0 &&
				<Column mt={6} style={{gap: 4}}>
					<Text style={{fontSize: 11, fontWeight: "bold", opacity: .8}}>
						{`4. loop repair attempts (${loopHistory.length}) -- these ran BEFORE the round was shown`}
					</Text>
					{loopHistory.map((entry, index)=>
						<RepairHistoryRow key={index} entry={entry} scores={scores} judgedByAttempt={judgedByAttempt}/>,
					)}
				</Column>}

			{/* 5. what the player actually read. This is the authoritative record of the round as played. */}
			<Column mt={6} style={{gap: 2}}>
				<Row center style={{gap: 8, flexWrap: "wrap"}}>
					<Text style={{fontSize: 11, opacity: .6}}>
						{loopHistory.length > 0 || manualHistory.length > 0
							? "5. what the player read (this is the round as it was played)"
							: "what the player read"}
					</Text>
					{playedFrom != null
						// the same text is above already, so point at it instead of repeating its numbers
						? <Text style={{fontSize: 11, opacity: .6}}>{`unchanged from ${playedFrom}`}</Text>
						: <TextScoreLabel scores={scores} judgedByAttempt={judgedByAttempt} text={playedText}/>}
				</Row>
				<Text style={{fontWeight: "bold"}}>{playedText}</Text>
				{playedLeaks.length > 0
					? <Text style={{fontSize: 11, color: "tomato"}}>
						{`copied verbatim from ${isReal ? "the journal" : "the generator's text"} (5+ words): ${playedLeaks.map(a=>`"${a}"`).join(", ")}`}
					</Text>
					: <Text style={{fontSize: 11, opacity: .45}}>{"no 5+-word run copied from the source"}</Text>}
			</Column>

			{/* 6. the manual retry chain, in order. Each entry starts from the one before it, so this is the one
			    place the sequence is readable end to end. */}
			{manualHistory.length > 0 &&
				<Column mt={6} style={{gap: 4}}>
					<Text style={{fontSize: 11, fontWeight: "bold", opacity: .8}}>
						{`6. manual retry attempts (${manualHistory.length}) -- these ran AFTER, in the debug view only`}
					</Text>
					{manualHistory.map((entry, index)=>
						// a manual retry's re-judge log is deliberately not persisted, so these rows never carry a score
						<RepairHistoryRow key={index} entry={entry} scores={scores} judgedByAttempt={judgedByAttempt}/>,
					)}
				</Column>}

			{/* the sandbox is where the manual chain has got TO -- so it follows the manual attempts, not the played
			    text. It only appears once a manual attempt actually changed something. */}
			{manualHistory.length > 0 && sandboxText != playedText &&
				<Column mt={6} style={{gap: 2, paddingLeft: 8, borderLeft: "2px solid rgba(120,180,255,.6)"}}>
					<Text style={{fontSize: 11, opacity: .6}}>{"result of the retry chain -- NOT what the player saw"}</Text>
					<Text style={{color: "lightblue"}}>{sandboxText}</Text>
				</Column>}

			{allHistory.length == 0 && <Text style={{fontSize: 11, opacity: .5, marginTop: 6}}>no repair attempts</Text>}

			<RetryControls round={round} slot={slot}/>
		</Column>
	);
});

const RepairHistoryRow = observer_mgl((props: {
	entry: SlotRepairAttempt,
	scores: AttemptScore[],
	judgedByAttempt: Map<number, string>,
})=>{
	const {entry, scores, judgedByAttempt} = props;
	const accent = entry.applied ? "lightgreen" : "orange";

	return (
		<Column style={{paddingLeft: 8, borderLeft: `2px solid ${accent}`, gap: 1}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontSize: 11, color: accent, fontWeight: "bold"}}>
					{`${entry.source} · attempt ${entry.attemptIndex + 1}`}
				</Text>
				<Text style={{fontSize: 11}}>{DescribeRepair(entry.repair)}</Text>
				<Text style={{fontSize: 11, color: accent}}>{entry.applied ? "applied" : "rejected"}</Text>
				{/* the score the RESULTING text then earned. Absent for a rejected attempt -- that text was never
				    judged, and showing the previous attempt's number here would misattribute it. */}
				<TextScoreLabel scores={scores} judgedByAttempt={judgedByAttempt} text={entry.applied ? entry.textAfter : null}/>
				{entry.outputTokens != null && <Text style={{fontSize: 11, opacity: .45}}>{`${entry.outputTokens} tok`}</Text>}
			</Row>
			<Text style={{fontSize: 11, opacity: .75}}>{entry.reason}</Text>
			{/* a rejected attempt's OUTPUT is the most useful line in this whole panel: it is the text the model
			    wrote and the reason it was not used */}
			{entry.textAfter != null && entry.textAfter != entry.textBefore &&
				<Text style={{fontSize: 11, opacity: entry.applied ? .85 : .6}}>
					{`${entry.applied ? "-> " : "rejected: "}${entry.textAfter}`}
				</Text>}
			{/* An APPLIED attempt whose text did not move. Without this line the row shows `applied` and then
			    nothing, which reads as "the generation was lost" -- when the truth is that the model handed the
			    sentence back unchanged. Older rounds still hold such entries (the no-op guard in `RepairRewrite`
			    is new), so this stays a display concern rather than being cleaned up in the data. */}
			{entry.applied && entry.textAfter != null && entry.textAfter == entry.textBefore &&
				<Text style={{fontSize: 11, opacity: .5, color: "orange"}}>
					{"(no change -- the model returned the sentence unchanged)"}
				</Text>}
			{entry.newSourceId != null &&
				<Text style={{fontSize: 11, opacity: .5}}>{`new source: ${entry.newSourceId}`}</Text>}
		</Column>
	);
});

// the retry
// ==========

/** Pick a remedy and re-run the judgement for this one bullet. The AUC shown afterwards is from re-judging
 * the story once with the change in place, which is the answer to "would this remedy have hidden it?". */
const RetryControls = observer_mgl((props: {round: EchoRound, slot: RoundSlot})=>{
	const {round, slot} = props;
	const isReal = slot.sourceKind == "real";

	const [repair, setRepair] = useState<JudgeRepair>(isReal ? "rewriteReal" : "resampleFake");
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState<{ok: boolean, message: string, sandboxText?: string}|null>(null);

	/** remedies that make sense for this bullet's class. A fake cannot be "rewritten into the world" (it has no
	 * source event to retain), and resampling a fake into a real would change the round's real count mid-game. */
	const available = (isReal ? ["rewriteReal", "resampleReal"] : ["resampleFake"]) as JudgeRepair[];
	const effective = available.includes(repair) ? repair : available[0];

	const run = async()=>{
		setRunning(true);
		setResult(null);
		try {
			const retry = await RetrySlotRepair_Live(round, slot.slotId, effective);
			if (!retry.ok) {
				setResult({ok: false, message: retry.error});
			} else {
				const metric = MetricFromLoopLog(retry.result, retry.round.slots, round.xShare ?? .5);
				const entry = retry.history;
				setResult({
					ok: entry.applied,
					message: `${entry.applied ? "applied" : "rejected"}: ${entry.reason}`
						+ (metric == null ? "" : ` · story AUC after re-judge: ${metric.auc.toFixed(3)}`),
					// the sandbox text is shown in the bullet above; this makes clear WHICH text moved
					sandboxText: retry.sandboxText,
				});
			}
		} catch (ex) {
			setResult({ok: false, message: `${ex}`});
		}
		setRunning(false);
	};

	return (
		<Row mt={6} center style={{gap: 8, flexWrap: "wrap"}}>
			{available.length > 1
				? (
					<Select displayType="dropdown" style={{fontSize: 11}}
						options={available.map(a=>({name: DescribeRepair(a), value: a as string}))}
						value={effective}
						onChange={(value: string)=>setRepair(value as JudgeRepair)}/>
				)
				: <Text style={{fontSize: 11, opacity: .7}}>{DescribeRepair(effective)}</Text>}
			<Button text={running ? "Retrying…" : "Retry this bullet"} enabled={!running} onClick={run}/>
			{running && <ProgressSpinner/>}
			{result != null &&
				<Text style={{fontSize: 11, color: result.ok ? "lightgreen" : "tomato"}}>{result.message}</Text>}
		</Row>
	);
});

// score labels
// ==========

/** Every judge score recorded for one bullet, tagged with the loop attempt it belongs to. */
type AttemptScore = {attemptIndex: number, judge: string, score: number};

/** The scores for one bullet as a compact label. Judges scoring the SAME text are separated by "," and a new
 * ATTEMPT by ";", numbered when there is more than one: "#1 0.70, 0.80; #2 0.82, 0.82" is two judges on the
 * first read and two on the second, not four judges on one. */
function DescribeAttemptScores(scores: AttemptScore[]): string|null {
	if (scores.length == 0) return null;

	const byAttempt = new Map<number, number[]>();
	for (const score of scores) {
		const group = byAttempt.get(score.attemptIndex);
		if (group == null) byAttempt.set(score.attemptIndex, [score.score]);
		else group.push(score.score);
	}

	const groups = [...byAttempt.keys()].sort((a, b)=>a - b)
		.map(attemptIndex=>({attemptIndex, values: byAttempt.get(attemptIndex)!}));
	// the number is only noise when there is a single attempt -- then the list length IS the judge-call count
	const showAttemptNumbers = groups.length > 1;
	return groups
		.map(group=>`${showAttemptNumbers ? `#${group.attemptIndex + 1} ` : ""}${group.values.map(value=>value.toFixed(2)).join(", ")}`)
		.join("; ");
}

/** "judge: 0.87, 0.82" beside the exact text that earned those numbers, or nothing when no judge ever read
 * that text (the original journal sentence and the shared reshape usually are never judged on their own). */
const TextScoreLabel = observer_mgl((props: {scores: AttemptScore[], judgedByAttempt: Map<number, string>, text: string|null})=>{
	const {scores, judgedByAttempt, text} = props;
	const label = DescribeAttemptScores(ScoresForText(scores, judgedByAttempt, text));
	if (label == null) return null;
	return <Text style={{fontSize: 11, opacity: .6}}>{`judge: ${label}`}</Text>;
});

/** The scores that belong to EXACTLY this text, matched against the text each attempt actually read rather
 * than against the attempt's position. A sentence can be read more than once (a repair that changes nothing
 * leaves it to be judged again), in which case every such attempt is listed. */
function ScoresForText(scores: AttemptScore[], judgedByAttempt: Map<number, string>, text: string|null): AttemptScore[] {
	if (text == null || text.length == 0) return [];
	return scores.filter(score=>judgedByAttempt.get(score.attemptIndex) == text);
}

/** The text each loop attempt was JUDGED on, keyed by attempt index: attempt 0 read the woven text, attempt k
 * read what the repair from k-1 left. Replayed from `repairHistory` so a label only lands on a text a judge
 * really saw (§9.1 re-reads the whole story each attempt). */
function JudgedTextsByAttempt(slot: RoundSlot, scores: AttemptScore[]): Map<number, string> {
	const result = new Map<number, string>();
	if (scores.length == 0) return result;

	const attempts = [...new Set(scores.map(a=>a.attemptIndex))].sort((a, b)=>a - b);
	const maxAttempt = attempts[attempts.length - 1];
	const withScores = new Set(attempts);
	const loopHistory = (slot.repairHistory ?? []).filter(a=>a.source == "loop");

	// `finalText` already includes every applied loop repair, so it is only a valid starting point when no
	// loop repair was recorded at all (a round judged before the history existed).
	let text = loopHistory.length == 0 ? DisplayText(slot) : PreLoopText(slot);
	for (let attemptIndex = 0; attemptIndex <= maxAttempt; attemptIndex++) {
		if (withScores.has(attemptIndex)) result.set(attemptIndex, text);
		// the repair chosen at the end of THIS attempt is what the next attempt reads
		for (const entry of loopHistory) {
			if (entry.attemptIndex != attemptIndex || !entry.applied || entry.textAfter == null) continue;
			text = entry.textAfter;
		}
	}
	return result;
}

/** The slot's text going INTO the judge loop: after weaving (§8.1), which is what the first judge reads. */
function PreLoopText(slot: RoundSlot): string {
	if (slot.wovenText.length > 0) return slot.wovenText;
	if (slot.reshapedText.length > 0) return slot.reshapedText;
	return DisplayText(slot);
}

/** The step the played text came from, or null when it matches nothing on display. Newest-first, mirroring
 * each step's own display condition so the back-reference never points at a hidden step. */
function DescribePlayedTextSource(slot: RoundSlot, originalText: string|null, loopHistory: SlotRepairAttempt[]): string|null {
	const played = DisplayText(slot);

	const lastApplied = [...loopHistory].reverse().find(a=>a.applied && a.textAfter == played);
	if (lastApplied != null) return `4. loop repair attempt ${lastApplied.attemptIndex + 1} (${DescribeRepair(lastApplied.repair)})`;
	if (slot.wovenText.length > 0 && slot.wovenText == played) return "3. woven";
	if (slot.reshapedText.length > 0 && slot.reshapedText == played) return "2. reshaped";
	if (originalText != null && originalText == played) return "1. original";
	return null;
}

// helpers
// ==========

/** The bullet's displayed text, with the same fallback chain `Round.SlotText` uses. */
function DisplayText(slot: RoundSlot): string {
	if (slot.finalText.length > 0) return slot.finalText;
	if (slot.wovenText.length > 0) return slot.wovenText;
	return slot.reshapedText;
}

/** The pre-transformation journal text behind a real slot (§4.2, the corpus row's `rawText`).
 *
 * A slot's `sourceId` is the CORPUS ROW id, not the journal `bulletId`, so this looks up by `id`. Returns
 * null when the row is gone (a re-index can delete a stale row), which the caller reports rather than
 * silently showing nothing. */
function GetOriginalBulletText(sourceId: string): string|null {
	const row = GetMyEchoCorpusBullets().find(a=>a.id == sourceId) ?? null;
	const raw = row?.rawText?.trim();
	return raw != null && raw.length > 0 ? raw : null;
}

/** What a slot's played text must not copy from: for a real, every raw bullet of its source dream; for a fake,
 * the generator's text (stored on the round). */
function GetLeakReferencesForSlot(round: EchoRound, slot: RoundSlot): string[] {
	if (slot.sourceKind == "real") {
		const rows = GetMyEchoCorpusBullets();
		const row = rows.find(a=>a.id == slot.sourceId);
		if (row == null) return [];
		return rows.filter(a=>a.dreamId == row.dreamId).map(a=>a.rawText ?? "").filter(a=>a.trim().length > 0);
	}
	const generated = (round.generatedBullets ?? []).find(a=>a.id == slot.sourceId);
	return generated == null ? [] : [generated.text];
}

/** The metric from a manual retry's own judge log, so the readout reports what those judges actually said
 * rather than the round's stored summary (which a manual experiment deliberately does not touch).
 *
 * Takes the round's slots because the metric needs the true labels, which only exist there. Returns null when
 * no judge produced scores -- a re-judge that failed says nothing about the remedy. */
function MetricFromLoopLog(result: {judgeLog: {judge: string, scores: Record<string, number>, bandMin?: number|n, bandMax?: number|n}[]}, slots: RoundSlot[]|n, xShare: number): JudgeMetric|null {
	const last = [...result.judgeLog].reverse().find(a=>a.judge != "heldOut" && Object.keys(a.scores).length > 0);
	if (last == null) return null;

	const band = NormalizeBand(last.bandMin ?? .4, last.bandMax ?? .6);
	return ComputeJudgeMetric(last.scores, slots, band, {xShare});
}

function MeanOf(values: number[]): number|null {
	if (values.length == 0) return null;
	return values.reduce((sum, value)=>sum + value, 0) / values.length;
}

/** The one-line verdict on a round, for the picker. */
function DescribeRoundVerdict(round: EchoRound): string {
	const summary = round.judgeSummary;
	if (summary == null) return "not judged";
	if (summary.landedInBand) return `in band (AUC ${summary.auc == null ? "n/a" : summary.auc.toFixed(2)})`;
	return `too easy (AUC ${summary.auc == null ? "n/a" : summary.auc.toFixed(2)})`;
}
